import { MapboxBasic } from 'components/MapBox/Basic';
import { AreaRating } from 'const/AreaRating';
import { MapZonesType } from 'const/Map/MapZonesType';
import { updateRatings } from 'fetch/fetchRatings';
import { useCourierPreferenceData } from 'hooks/useCourierPreferenceData';
import React, { ChangeEvent, useState } from 'react';
import Swal from 'sweetalert2';
import { CourierJson } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import { bemComponent } from './bemComponent';
import { CourierSelection } from './CourierSelection/CourierSelection';
import { DeliveriesConfigForm } from './DeliveriesConfigForm';
import { RatingSelection } from './RatingSelection/RatingSelection';
import { Section } from './Section/section';

interface PreferredAreasWrapperProps {
  region: Region;
  courierList: CourierJson[];
  selectedCourier: CourierJson;
  onChangeSelectedCourier: (
    _: ChangeEvent<any>,
    value: string | CourierJson
  ) => void;
}

export const PreferredAreasWrapper = ({
  region,
  onChangeSelectedCourier,
  ...props
}: PreferredAreasWrapperProps) => {
  const bem = bemComponent('preferred-areas');

  const [courierPreferenceEndpointDraft, setCourierPreferenceEndpointDraft] =
    useState<{
      endpoint?: {
        lon: number;
        lat: number;
      };
      endpointAddress?: string;
    }>();
  const [pinpointDraggable, setPinpointDraggable] = useState(false);

  const courierPreferenceData = useCourierPreferenceData(props.selectedCourier);

  const updatePinpointDraggable = (newStatus: boolean) => {
    setPinpointDraggable(newStatus);
  };

  const onChangeCourierPreferenceEndpointDraft = (
    lon?: number,
    lat?: number,
    name?: string
  ) => {
    if (lon && lat) {
      setCourierPreferenceEndpointDraft({
        endpoint: { lon, lat },
        endpointAddress: name || null,
      });
    } else {
      setCourierPreferenceEndpointDraft(undefined);
    }
  };

  const onClickZonesOverlay = async (
    selectedZone: string | number,
    currentFeatureState: any
  ) => {
    /* ZonesOverlay only enabled when the Courier has been selected */
    if (props.selectedCourier) {
      const currentZoneRating = currentFeatureState['rating'];
      let newRating: AreaRating | null;

      /**
       * When the selected zones is empty, `newRating` should be changed to `activeRatingType`.
       * When the selected zones is not empty :
       * - selected zones === LIKE and activeRatingType === LIKE => Unselect zones
       * - selected zones === LIKE and activeRatingType === DISLIKE => change `newRating` to DISLIKE
       * - selected zones === DISLIKE and activeRatingType === DISLIKE => Unselect zones
       * - selected zones === DISLIKE and activeRatingType === LIKE => change `newRating` to LIKE
       */
      if (currentZoneRating === courierPreferenceData.activeRatingType) {
        newRating = null;
      } else {
        newRating = courierPreferenceData.activeRatingType;
      }

      try {
        /* Save to backend */
        await updateRatings({
          zoneId: selectedZone.toString(),
          courierId: props.selectedCourier.id,
          newRating,
        });

        /* Update the courier's selected area */
        courierPreferenceData.refreshCourierSelectedArea();
      } catch (error) {
        /* Error while updating the courier's zone preference */
        /* Currently the error message has been handled inside `updateRatings` function */
      }
    } else {
      /* Show warning to user for selecting the courier before selecting the zones */
      Swal.fire({
        title: 'No courier!',
        icon: 'error',
        html: '<div>Please select a courier to assign the selected zone to.</div>',
      });
    }
  };

  const onDragEndPinpoint = (newPinpointData: { lon: number; lat: number }) => {
    onChangeCourierPreferenceEndpointDraft(
      newPinpointData.lon,
      newPinpointData.lat
    );
  };

  return (
    <>
      <div className={bem.element('sidebar')}>
        <>
          <Section>
            <h3>Find courier</h3>
            <CourierSelection
              list={props.courierList}
              selected={props.selectedCourier}
              onSelected={onChangeSelectedCourier}
            />
          </Section>
          {props.selectedCourier ? (
            <>
              <Section>
                <h3>Notes</h3>
                {props.selectedCourier?.notes ? (
                  <p>{props.selectedCourier?.notes}</p>
                ) : (
                  <p>There are currently no notes about this courier.</p>
                )}
              </Section>

              <Section>
                <h3>Likes / dislikes</h3>
                <RatingSelection
                  selected={courierPreferenceData.activeRatingType}
                  areas={courierPreferenceData.selectedAreas || []}
                  onChange={courierPreferenceData.onChangeActiveRatingType}
                />
              </Section>

              <Section>
                <DeliveriesConfigForm
                  courierId={props.selectedCourier?.id}
                  courierPreference={courierPreferenceData.essentialData}
                  courierPreferenceEndpointDraft={
                    courierPreferenceEndpointDraft
                  }
                  updatePinpointDraggable={updatePinpointDraggable}
                  onChangeCourierPreferenceEndpointDraft={
                    onChangeCourierPreferenceEndpointDraft
                  }
                  refreshData={courierPreferenceData.refreshEssentialData}
                />
              </Section>
            </>
          ) : (
            <Section>
              <h4>Please select a courier</h4>
            </Section>
          )}
        </>
      </div>

      <MapboxBasic
        hasZonesOverlay
        hasPinpoint={
          !!courierPreferenceEndpointDraft?.endpoint ||
          !!courierPreferenceData.essentialData?.endpoint ||
          pinpointDraggable
        }
        region={region}
        zonesOverlayObjects={courierPreferenceData.selectedAreas || []}
        zonesOverlayType={MapZonesType.PreferredArea}
        pinpointData={
          courierPreferenceEndpointDraft?.endpoint ||
          courierPreferenceData.essentialData?.endpoint
        }
        pinpointDraggable={pinpointDraggable}
        pinpointIsUsingHubAsDefault={pinpointDraggable}
        onClickZonesOverlay={onClickZonesOverlay}
        onDragEndPinpoint={onDragEndPinpoint}
      />
    </>
  );
};
